import * as THREE from 'three';
import {
  CAMERA_FOV,
  CAMERA_NEAR,
  CAMERA_FAR,
  CAMERA_INITIAL_Z,
  ENABLE_ANTIALIASING,
  ENABLE_SHADOWS
} from '@config/constants.js';

/**
 * SceneManager - Manages Three.js scene, camera, renderer, and lighting
 *
 * Responsibilities:
 * - Initialize and configure Three.js scene
 * - Set up camera with proper aspect ratio
 * - Configure WebGL renderer with antialiasing
 * - Add lighting (ambient + directional)
 * - Handle window resize events
 */
export class SceneManager {
  constructor(container) {
    if (!container) {
      throw new Error('SceneManager requires a container element');
    }

    this.container = container;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.lights = {};

    this._initialize();
  }

  /**
   * Initialize the Three.js scene, camera, renderer, and lighting
   * @private
   */
  _initialize() {
    this._createScene();
    this._createCamera();
    this._createRenderer();
    this._createLighting();
    this._setupResizeHandler();
  }

  /**
   * Create the Three.js scene
   * @private
   */
  _createScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1F2020); // Dark grey background for card visibility
  }

  /**
   * Create and configure the perspective camera
   * @private
   */
  _createCamera() {
    const aspect = this.container.clientWidth / this.container.clientHeight;

    this.camera = new THREE.PerspectiveCamera(
      CAMERA_FOV,
      aspect,
      CAMERA_NEAR,
      CAMERA_FAR
    );

    this.camera.position.set(0, 0, CAMERA_INITIAL_Z);
    this.camera.lookAt(0, 0, 0);
  }

  /**
   * Create and configure the WebGL renderer
   * @private
   */
  _createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: ENABLE_ANTIALIASING,
      alpha: false
    });

    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = ENABLE_SHADOWS;

    if (ENABLE_SHADOWS) {
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    // Set output color space to sRGB for accurate color reproduction
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this._updateRendererSize();
    this.container.appendChild(this.renderer.domElement);
  }

  /**
   * Create lighting setup for the scene
   * Ambient light for overall illumination + directional light for depth
   * @private
   */
  _createLighting() {
    // Ambient light - provides base illumination
    this.lights.ambient = new THREE.AmbientLight(0xffffff, 2.9);
    this.scene.add(this.lights.ambient);

    // Directional light - creates depth and shadows
    this.lights.directional = new THREE.DirectionalLight(0xffffff, 0.8);
    this.lights.directional.position.set(5, 5, 5);

    if (ENABLE_SHADOWS) {
      this.lights.directional.castShadow = true;
      this.lights.directional.shadow.mapSize.width = 1024;
      this.lights.directional.shadow.mapSize.height = 1024;
    }

    this.scene.add(this.lights.directional);

    // Optional: Add a subtle fill light from the opposite side
    this.lights.fill = new THREE.DirectionalLight(0xffffff, 0.3);
    this.lights.fill.position.set(-5, -5, -5);
    this.scene.add(this.lights.fill);
  }

  /**
   * Update renderer size based on container dimensions
   * @private
   */
  _updateRendererSize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Set up window resize event handler
   * @private
   */
  _setupResizeHandler() {
    this._resizeHandler = () => this._updateRendererSize();
    window.addEventListener('resize', this._resizeHandler);
  }

  /**
   * Add an object to the scene
   * @param {THREE.Object3D} object - The object to add
   */
  add(object) {
    this.scene.add(object);
  }

  /**
   * Remove an object from the scene
   * @param {THREE.Object3D} object - The object to remove
   */
  remove(object) {
    this.scene.remove(object);
  }

  /**
   * Render the scene
   */
  render() {
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Update scene background color
   * @param {number|string} color - Color value (hex number or CSS string)
   */
  setBackgroundColor(color) {
    this.scene.background = new THREE.Color(color);
  }

  /**
   * Get the renderer's DOM element
   * @returns {HTMLCanvasElement}
   */
  getDomElement() {
    return this.renderer.domElement;
  }

  /**
   * Clean up resources and event listeners
   */
  dispose() {
    window.removeEventListener('resize', this._resizeHandler);

    // Dispose renderer
    this.renderer.dispose();

    // Remove canvas from DOM
    if (this.renderer.domElement.parentElement) {
      this.renderer.domElement.parentElement.removeChild(this.renderer.domElement);
    }

    // Clear references
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.lights = null;
  }
}
